// /portal/locations — the FIRST portal write surface.
//
// Portal users can: read their company's locations (portal_read on
// locations from 20260607003457) AND insert new locations for their
// own company. The portal_insert policy from 20260607030000 enforces
// company + workspace match + created_via_portal=true at the row
// level; the client side just supplies the right values.
//
// They cannot edit or delete (no UPDATE/DELETE policies for portal
// sessions). Form is add-only; rows are read-only after creation.
//
// Mobile-first: single-column form, tokens only.

import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/lib/auth'
import { formatRelativeDate } from '@/lib/utils'
import { MapPin, Plus, X, Loader2, AlertCircle, CheckCircle2, Clock } from 'lucide-react'
import { toast } from 'sonner'
import { PortalSkeletonList } from '@/pages/portal/PortalSkeleton'

interface LocationRow {
  id: string
  name: string
  address_line1: string
  address_line2: string | null
  city: string
  state: string
  zip: string
  country: string | null
  status: string | null
  store_number: string | null
  region: string | null
  district: string | null
  site_contact_name: string | null
  site_contact_phone: string | null
  access_notes: string | null
  parking_instructions: string | null
  gate_code: string | null
  notes: string | null
  created_via_portal: boolean
  portal_acknowledged_at: string | null
  created_at: string
}

export default function PortalLocations() {
  const { portalUser } = useAuth()
  const queryClient = useQueryClient()
  const [showForm, setShowForm] = useState(false)

  const { data: locations = [], isLoading } = useQuery({
    queryKey: ['portal-locations', portalUser?.company_id],
    queryFn: async () => {
      if (!portalUser?.company_id) return []
      const { data, error } = await supabase
        .from('locations')
        .select('id, name, address_line1, address_line2, city, state, zip, country, status, store_number, region, district, site_contact_name, site_contact_phone, access_notes, parking_instructions, gate_code, notes, created_via_portal, portal_acknowledged_at, created_at')
        .eq('company_id', portalUser.company_id)
        .order('created_at', { ascending: false })
      if (error) throw error
      return (data ?? []) as LocationRow[]
    },
    enabled: !!portalUser?.company_id,
  })

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
        <p style={{ fontSize: 12, color: 'var(--ink-tertiary)', margin: 0 }}>
          {locations.length === 0 ? 'No locations yet.' : `${locations.length} location${locations.length === 1 ? '' : 's'}`}
        </p>
        {!showForm && (
          <button type="button" onClick={() => setShowForm(true)}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 14px', background: 'var(--accent)', border: 'none', borderRadius: 8, color: 'var(--accent-ink)', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>
            <Plus style={{ width: 12, height: 12 }} />
            Add location
          </button>
        )}
      </div>

      {showForm && <AddLocationForm onClose={() => setShowForm(false)} onSaved={() => {
        queryClient.invalidateQueries({ queryKey: ['portal-locations', portalUser?.company_id] })
        setShowForm(false)
      }} />}

      {isLoading ? (
        <PortalSkeletonList count={3} height={80} />
      ) : locations.length === 0 && !showForm ? (
        <div style={{ padding: '32px 16px', textAlign: 'center' }}>
          <MapPin style={{ width: 28, height: 28, color: 'var(--ink-tertiary)', margin: '0 auto 12px', display: 'block' }} />
          <p style={{ fontSize: 14, fontWeight: 700, color: 'var(--ink-secondary)', margin: '0 0 4px' }}>No locations yet</p>
          <p style={{ fontSize: 12, color: 'var(--ink-tertiary)', margin: 0 }}>Add a site and your service provider can schedule visits there straight away.</p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {locations.map(loc => <LocationCard key={loc.id} loc={loc} />)}
        </div>
      )}
    </div>
  )
}

function LocationCard({ loc }: { loc: LocationRow }) {
  return (
    <div style={{ background: 'var(--surface)', border: '1px solid var(--border-subtle)', borderRadius: 10, padding: '12px 14px', display: 'flex', alignItems: 'flex-start', gap: 10 }}>
      <div style={{ width: 32, height: 32, borderRadius: 8, background: 'var(--positive-dim)', border: '1px solid var(--positive-border)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
        <MapPin style={{ width: 14, height: 14, color: 'var(--positive)' }} />
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 2, flexWrap: 'wrap' }}>
          <p style={{ fontSize: 13, fontWeight: 700, color: 'var(--ink-primary)', margin: 0 }}>{loc.name}</p>
          {loc.store_number && <span style={{ fontSize: 10, fontFamily: 'monospace', color: 'var(--ink-tertiary)' }}>{loc.store_number}</span>}
          {loc.created_via_portal && (
            // Surface "Pending review" until staff acknowledge — gives the client
            // an honest signal that the row hasn't been touched by the provider yet.
            <span style={{ fontSize: 9, fontWeight: 700, padding: '2px 7px', borderRadius: 100,
              background: loc.portal_acknowledged_at ? 'var(--positive-dim)' : 'var(--warning-dim)',
              color: loc.portal_acknowledged_at ? 'var(--positive)' : 'var(--warning)',
              border: `1px solid ${loc.portal_acknowledged_at ? 'var(--positive-border)' : 'var(--warning-border)'}`,
              display: 'inline-flex', alignItems: 'center', gap: 3 }}>
              {loc.portal_acknowledged_at
                ? <><CheckCircle2 style={{ width: 9, height: 9 }} /> Acknowledged</>
                : <><Clock style={{ width: 9, height: 9 }} /> Pending review</>}
            </span>
          )}
        </div>
        <p style={{ fontSize: 12, color: 'var(--ink-secondary)', margin: 0 }}>
          {loc.address_line1}{loc.address_line2 ? `, ${loc.address_line2}` : ''} · {loc.city}, {loc.state} {loc.zip}
        </p>
        <p style={{ fontSize: 10, color: 'var(--ink-tertiary)', margin: '3px 0 0' }}>Added {formatRelativeDate(loc.created_at)}</p>
      </div>
    </div>
  )
}

function AddLocationForm({ onClose, onSaved }: { onClose: () => void; onSaved: () => void }) {
  const { portalUser } = useAuth()
  const [form, setForm] = useState({
    name: '', address_line1: '', address_line2: '',
    city: '', state: '', zip: '',
    site_contact_name: '', site_contact_phone: '',
    access_notes: '', notes: '',
  })
  const set = (k: keyof typeof form, v: string) => setForm(p => ({ ...p, [k]: v }))

  const saveMutation = useMutation({
    mutationFn: async () => {
      if (!portalUser) throw new Error('Portal session missing')
      // Required matches staff LocationModal: name, address_line1, city, state, zip.
      // RLS will reject if workspace_id / company_id mismatch the session helpers,
      // which is the load-bearing safety — we just supply what the policy expects.
      const { error } = await supabase.from('locations').insert({
        workspace_id: portalUser.workspace_id,
        company_id: portalUser.company_id,
        name: form.name.trim(),
        address_line1: form.address_line1.trim(),
        address_line2: form.address_line2.trim() || null,
        city: form.city.trim(),
        state: form.state.trim(),
        zip: form.zip.trim(),
        site_contact_name: form.site_contact_name.trim() || null,
        site_contact_phone: form.site_contact_phone.trim() || null,
        access_notes: form.access_notes.trim() || null,
        notes: form.notes.trim() || null,
        created_via_portal: true,
        created_by_portal_user_id: portalUser.id,
      })
      if (error) throw error
    },
    onSuccess: () => { toast.success('Location added'); onSaved() },
    onError: (e: any) => toast.error(e.message || 'Failed to add location'),
  })

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!form.name.trim()) { toast.error('Location name is required'); return }
    if (!form.address_line1.trim()) { toast.error('Address is required'); return }
    if (!form.city.trim()) { toast.error('City is required'); return }
    if (!form.state.trim()) { toast.error('State is required'); return }
    if (!form.zip.trim()) { toast.error('ZIP is required'); return }
    saveMutation.mutate()
  }

  return (
    <form onSubmit={onSubmit}
      style={{ background: 'var(--surface)', border: '1px solid var(--accent-border)', borderRadius: 12, padding: 16, display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <p style={{ fontSize: 13, fontWeight: 800, color: 'var(--ink-primary)', margin: 0 }}>Add location</p>
        <button type="button" onClick={onClose} aria-label="Cancel"
          style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--ink-tertiary)', padding: 0 }}>
          <X style={{ width: 14, height: 14 }} />
        </button>
      </div>

      <FormField label="Location name *" required>
        <input value={form.name} onChange={e => set('name', e.target.value)} autoFocus style={inp} />
      </FormField>
      <FormField label="Address line 1 *" required>
        <input value={form.address_line1} onChange={e => set('address_line1', e.target.value)} style={inp} />
      </FormField>
      <FormField label="Address line 2">
        <input value={form.address_line2} onChange={e => set('address_line2', e.target.value)} style={inp} />
      </FormField>
      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: 8 }}>
        <FormField label="City *" required>
          <input value={form.city} onChange={e => set('city', e.target.value)} style={inp} />
        </FormField>
        <FormField label="State *" required>
          <input value={form.state} onChange={e => set('state', e.target.value)} style={inp} />
        </FormField>
        <FormField label="ZIP *" required>
          <input value={form.zip} onChange={e => set('zip', e.target.value)} style={inp} />
        </FormField>
      </div>

      <div style={{ borderTop: '1px solid var(--border-subtle)', paddingTop: 10 }}>
        <p style={{ fontSize: 10, fontWeight: 700, color: 'var(--ink-tertiary)', textTransform: 'uppercase', letterSpacing: '0.06em', margin: '0 0 8px' }}>Site contact (optional)</p>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
          <FormField label="Name"><input value={form.site_contact_name} onChange={e => set('site_contact_name', e.target.value)} style={inp} /></FormField>
          <FormField label="Phone"><input value={form.site_contact_phone} onChange={e => set('site_contact_phone', e.target.value)} style={inp} /></FormField>
        </div>
      </div>

      <FormField label="Access notes">
        <textarea value={form.access_notes} onChange={e => set('access_notes', e.target.value)} rows={2}
          placeholder="Loading dock around back, ring buzzer"
          style={{ ...inp, resize: 'none', lineHeight: 1.5 }} />
      </FormField>
      <FormField label="Notes">
        <textarea value={form.notes} onChange={e => set('notes', e.target.value)} rows={2}
          placeholder="Anything worth remembering"
          style={{ ...inp, resize: 'none', lineHeight: 1.5 }} />
      </FormField>

      <p style={{ fontSize: 10, color: 'var(--ink-tertiary)', margin: 0, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
        <AlertCircle style={{ width: 10, height: 10 }} />
        Your service provider will see this location flagged for review.
      </p>

      <div style={{ display: 'flex', gap: 8 }}>
        <button type="button" onClick={onClose} disabled={saveMutation.isPending}
          style={{ flex: 1, padding: '10px', background: 'var(--hover-bg)', border: '1px solid var(--border-default)', borderRadius: 8, color: 'var(--ink-secondary)', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
          Cancel
        </button>
        <button type="submit" disabled={saveMutation.isPending}
          style={{ flex: 1, padding: '10px', background: 'var(--accent)', border: 'none', borderRadius: 8, color: 'var(--accent-ink)', fontSize: 13, fontWeight: 700, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
          {saveMutation.isPending && <Loader2 style={{ width: 13, height: 13, animation: 'spin 1s linear infinite' }} />}
          {saveMutation.isPending ? 'Adding…' : 'Add location'}
        </button>
      </div>
    </form>
  )
}

function FormField({ label, required, children }: { label: string; required?: boolean; children: React.ReactNode }) {
  return (
    <div>
      <label style={{ fontSize: 10, fontWeight: 600, color: 'var(--ink-tertiary)', display: 'block', marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
        {label}{required && <span style={{ color: 'var(--danger)', marginLeft: 2 }} aria-hidden="true">·</span>}
      </label>
      {children}
    </div>
  )
}

const inp: React.CSSProperties = {
  width: '100%', padding: '9px 11px',
  background: 'var(--field-bg)',
  border: '1px solid var(--border-default)',
  borderRadius: 8, fontSize: 13, color: 'var(--ink-primary)',
  outline: 'none', boxSizing: 'border-box',
}
